import type { HydratedUser } from "@packsize/identity-api-sdk";

import { type LicenseTier, licenseTierSchema } from "../schemas";
import type { LicensedProductCategoryName } from "../types";

import { exhaustiveMatchingGuard } from "./switch";

const licenseTiers: Record<LicenseTier, number> = {
	unlicensed: 0,
	basic: 1,
	essential: 1,
	professional: 2,
	enterprise: 3,
};

export const checkLicenseTier = (maybeUserLicenseTier: unknown, requiredTier: LicenseTier): boolean => {
	const validatedLicenseTier = licenseTierSchema.safeParse(maybeUserLicenseTier);

	if (validatedLicenseTier.success === false) {
		return false;
	}

	return licenseTiers[requiredTier] <= licenseTiers[validatedLicenseTier.data];
};

export const hasLicenseByTier = (
	user: HydratedUser | null | undefined,
	product: LicensedProductCategoryName,
	tier: LicenseTier,
): boolean => {
	switch (product) {
		case "Core":
			return checkLicenseTier(user?.coreLicenseTier, tier);
		case "DIM":
			return checkLicenseTier(user?.dimensioningLicenseTier, tier);
		case "Reporting":
			return checkLicenseTier(user?.reportingLicenseTier, tier);
		default:
			return exhaustiveMatchingGuard(product, "Unknown product");
	}
};

export const hasLicenseByFlag = (
	user: HydratedUser | null | undefined,
	licenseFeatureFlag: string | string[],
): boolean => {
	if (user === null || user === undefined) {
		return false;
	}

	if (Array.isArray(licenseFeatureFlag)) {
		return licenseFeatureFlag.some((f) => user.featureFlags[f] === "True");
	}

	return user.featureFlags[licenseFeatureFlag] === "True";
};

export const hasLicense = (
	user: HydratedUser | null | undefined,
	productCategory: LicensedProductCategoryName,
	tier: LicenseTier,
	licenseFeatureFlag?: string | string[],
): boolean => {
	const byTier = hasLicenseByTier(user, productCategory, tier);
	const byFlag = licenseFeatureFlag ? hasLicenseByFlag(user, licenseFeatureFlag) : false;

	return byTier || byFlag;
};
